import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description: string;
  canonical?: string;
  robots?: string; // e.g. "index, follow" or "noindex, follow"
  ogImage?: string; // per-page social preview image override
  ogType?: string; // e.g. "website" | "article"
}

function upsertMeta(
  selector: string,
  attr: string,
  name: string,
  content: string,
) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Sets document title, meta description, canonical link, robots meta,
 * Open Graph tags (incl. per-page og:image override), and Twitter Card
 * tags on mount. Prevents keyword cannibalization and duplicate metadata
 * by ensuring every page has unique, descriptive tags that update on
 * route change.
 */
export function useSEO({
  title,
  description,
  canonical,
  robots = "index, follow",
  ogImage,
  ogType = "website",
}: SEOOptions) {
  useEffect(() => {
    document.title = title;

    // Standard meta
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[name="robots"]', "name", "robots", robots);

    // Open Graph
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description,
    );
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
    if (canonical) {
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    }

    // Twitter Card
    upsertMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description,
    );
    if (ogImage) {
      upsertMeta(
        'meta[name="twitter:image"]',
        "name",
        "twitter:image",
        ogImage,
      );
    }

    // Canonical link
    if (canonical) {
      upsertLink("canonical", canonical);
    }
  }, [title, description, canonical, robots, ogImage, ogType]);
}
